@file:OptIn(ExperimentalResourceApi::class)

package ui.company.detail

import androidx.compose.runtime.*
import common.frontend.api.*
import compose.assets.Svg
import compose.assets.svg.*
import io.Repository
import kotlinx.coroutines.flow.MutableStateFlow
import kotlinx.coroutines.flow.StateFlow
import kotlinx.coroutines.launch
import org.jetbrains.compose.resources.ExperimentalResourceApi
import ssoft.frontend.generated.resources.*
import tk.mallumo.compose.navigation.CompanyContentLayerBottom
import tk.mallumo.compose.navigation.CompanyContentLayerTop
import tk.mallumo.compose.navigation.Graph
import tk.mallumo.compose.navigation.asBundle
import tk.mallumo.compose.navigation.viewmodel.NavigationViewModel
import tk.mallumo.compose.navigation.viewmodel.VM
import tk.mallumo.compose.navigation.viewmodel.viewModel
import tk.mallumo.utils.DataState
import tk.mallumo.utils.mutableDataState
import tools.TabEntry
import tools.printStackTraceInDebug
import ui.app.assign.AppAssignListingArgs
import ui.app.assign.AppAssignListingVM
import ui.campaign.CampaignListingArgs
import ui.campaign.CampaignListingVM
import ui.dashboard.ArgsDashboard
import ui.device.DeviceListingArgs
import ui.location.LocationListingArgs
import ui.resource.ResourceListingArgs
import ui.snackBarStateGlobal
import ui.user.UserListingArgs

@VM
class CompanyVM : NavigationViewModel() {


    private lateinit var scopeUi: CompanyScope

    var state by mutableDataState<CompanyScope.Wrapper>()
        private set

    enum class TabType {
        INFO,
        DASHBOARD,
        LAYER_TOP,
        LAYER_BOTTOM,
        MEDIA,
        DEVICES,
        LOCATION,
        USER,
    }


    var tabsScreenSmall by mutableStateOf(listOf<TabEntry<TabType>>())
        private set

    var tabsScreenLarge by mutableStateOf(listOf<TabEntry<TabType>>())
        private set

    private val _selectedTabSmall = MutableStateFlow<TabEntry<TabType>?>(null)
    val selectedTabSmall: StateFlow<TabEntry<TabType>?> get() = _selectedTabSmall


    private val _selectedTabLarge = MutableStateFlow<TabEntry<TabType>?>(null)
    val selectedTabLarge: StateFlow<TabEntry<TabType>?> get() = _selectedTabLarge

    sealed interface Action {

        data object Save : Action
        data object Delete : Action
        companion object {

            @Composable
            fun rememberAction(vm: CompanyVM): (Action) -> Unit {
                val composableContent: MutableState<(@Composable () -> Unit)?> = remember {
                    mutableStateOf(null, policy = neverEqualPolicy())
                }

                composableContent.value?.invoke()

                return remember {
                    { act ->
                        when (act) {
                            Save -> vm.save(composableContent)
                            Delete -> vm.delete()
                        }
                    }
                }
            }
        }
    }

    fun init(scopeUi: CompanyScope) {
        this.scopeUi = scopeUi
        if (state is DataState.Result) return

        state = DataState.Loading()

        scope.launch {
            configureTabs(scopeUi)
            runCatching {
                val data = Repository.api.company.single(scopeUi.args.companyId)
                state = DataState.Result(
                    CompanyScope.Wrapper(
                        canEdit = scopeUi.login.canCompanyEdit(scopeUi.args.companyId),
                        canAdd = scopeUi.login.canCompanyAdd(),
                        canDelete = scopeUi.login.canCompanyDelete(scopeUi.args.companyId) && data.main == 0,
                        data = data
                    )
                )
            }.onFailure {
                it.printStackTraceInDebug()
                state = DataState.Error(it.message!!, throwable = it)
            }
        }
    }

    private fun save(composableContent: MutableState<@Composable() (() -> Unit)?>) {
        scope.launch {
            state.entry?.runCatching {
                Repository.api.company.edit(data)
                scopeUi.vmGlobal.reloadCompanyWithLocation(scopeUi.login)
                this
            }?.onFailure {
                it.printStackTraceInDebug()
                snackBarStateGlobal.showSnackbar(
                    it.message!!,
                    withDismissAction = true
                )
            }?.onSuccess {
                it.updateOrigin()
                val companyId = scopeUi.args.companyId

                composableContent.value = {
                    scopeUi.nav.findChildNavigation(Graph.CompanyContentLayerTop)
                        ?.viewModel(CampaignListingVM::class, companyId.toString())
                        ?.reload(companyId)

                    scopeUi.nav.findChildNavigation(Graph.CompanyContentLayerBottom)
                        ?.viewModel(AppAssignListingVM::class, companyId.toString())
                        ?.reloadVariants(companyId)

                    LaunchedEffect(Unit) {
                        composableContent.value = null
                    }
                }
            }
        }
    }

    private fun delete() {
        scope.launch {
            runCatching {
                Repository.api.company.delete(scopeUi.args.companyId)
                scopeUi.vmGlobal.reloadCompanyWithLocation(scopeUi.login)
            }.onSuccess {
                scopeUi.nav.up()
            }.onFailure {
                it.printStackTraceInDebug()
                snackBarStateGlobal.showSnackbar(
                    it.message!!,
                    withDismissAction = true
                )
            }
        }
    }


    private fun configureTabs(scopeUi: CompanyScope) {
        tabsScreenSmall = buildTabs(true, scopeUi) {
            _selectedTabSmall.value = it
        }
        tabsScreenLarge = buildTabs(false, scopeUi) {
            _selectedTabLarge.value = it
        }

        tabsScreenSmall.firstOrNull()
            ?.takeIf { tabsScreenSmall.none { it.tag == selectedTabSmall.value?.tag } }
            ?.also { _selectedTabSmall.value = it }

        tabsScreenLarge.firstOrNull()
            ?.takeIf { tabsScreenLarge.none { it.tag == selectedTabLarge.value?.tag } }
            ?.also { _selectedTabLarge.value = it }
    }

    private fun buildTabs(
        withMainScreen: Boolean,
        scopeUi: CompanyScope,
        onClick: (TabEntry<TabType>) -> Unit
    ): List<TabEntry<TabType>> {
        return buildList {
            if (withMainScreen) {
                add(
                    TabEntry(
                        icon = Svg.Information_slab_circle_outline,
                        label = Res.string.company_detail__info__title,
                        onClick = onClick,
                        tag = TabType.INFO
                    )
                )
            }
            if (scopeUi.login.dashboards.isNotEmpty()) {
                add(
                    TabEntry(
                        icon = Svg.View_dashboard,
                        label = Res.string.home_panel__dashboard_my,
                        onClick = onClick,
                        args = ArgsDashboard(
                            serverId = scopeUi.login.server.id,
                            companyId = scopeUi.args.companyId
                        ).asBundle(),
                        tag = TabType.DASHBOARD
                    )
                )
            }
            if ((scopeUi.login.canLayerTopView(scopeUi.args.companyId))) {
                add(
                    TabEntry(
                        icon = Svg.Advertisements,
                        label = Res.string.company_detail__layer_top__title,
                        onClick = onClick,
                        args = CampaignListingArgs(scopeUi.args.companyId).asBundle(),
                        tag = TabType.LAYER_TOP
                    )
                )
            }
            if ((scopeUi.login.canLayerBottomView(scopeUi.args.companyId))) {
                add(
                    TabEntry(
                        icon = Svg.Application_brackets_outline,
                        label = Res.string.company_detail__layer_bottom__title,
                        onClick = onClick,
                        args = AppAssignListingArgs(scopeUi.args.companyId).asBundle(),
                        tag = TabType.LAYER_BOTTOM
                    )
                )
            }
            if ((scopeUi.login.canResourceView(scopeUi.args.companyId))) {
                add(
                    TabEntry(
                        icon = Svg.Multimedia,
                        label = Res.string.company_detail__media__title,
                        onClick = onClick,
                        args = ResourceListingArgs(scopeUi.args.companyId).asBundle(),
                        tag = TabType.MEDIA
                    )
                )
            }
            if ((scopeUi.login.canDeviceInfoView(scopeUi.args.companyId))) {
                add(
                    TabEntry(
                        icon = Svg.Device,
                        label = Res.string.company_detail__device__title,
                        onClick = onClick,
                        args = DeviceListingArgs(companyId = scopeUi.args.companyId).asBundle(),
                        tag = TabType.DEVICES
                    )
                )
            }

            if ((scopeUi.login.canLocationView(scopeUi.args.companyId))) {
                add(
                    TabEntry(
                        icon = Svg.Marker,
                        label = Res.string.company_detail__location__title,
                        onClick = onClick,
                        args = LocationListingArgs(scopeUi.args.companyId).asBundle(),
                        tag = TabType.LOCATION
                    )
                )
            }

            if ((scopeUi.login.canUserView(scopeUi.args.companyId))) {
                add(
                    TabEntry(
                        icon = Svg.Account,
                        label = Res.string.company_detail__user__title,
                        onClick = onClick,
                        args = UserListingArgs(scopeUi.args.companyId).asBundle(),
                        tag = TabType.USER
                    )
                )
            }
        }
    }

    override fun onRelease() {
        state = DataState.Idle()
    }

}
